import JsonApi, {_getUrlBase} from "../../../../api/jsonApi";
import {getCommonVisualizerType} from "../../../../config";

// Mobile photo handoff: the desktop shows a QR code that opens /qr.html on the phone,
// the phone uploads a photo for the GUID and the desktop polls until the image url is returned.

export const MOBILE_PHOTO_POLL_INTERVAL = 5000;
export const MOBILE_PHOTO_POLL_TIMEOUT = 10 * 60 * 1000; // stop polling after 10 minutes
export const MOBILE_PHOTO_MAX_NETWORK_ERRORS = 5; // consecutive failures before giving up

export type MobilePhotoPollingError = "notFound" | "network";

type MobilePhotoPollingCallbacks = {
  onImage: (imageUrl: string) => void;
  onExpired: () => void;
  onError: (reason: MobilePhotoPollingError) => void;
};

const getHttpStatus = (error: any): number | undefined => error?.response?.status;

// every call returns a new GUID - request a fresh one each time the QR panel is opened
export const requestMobilePhotoGuid = async (jobId?: number | null): Promise<string> => {
  const response = await JsonApi.getMobilePhotoGuid(jobId || null);
  const guid = response?.data;
  if (!guid || typeof guid !== "string") {
    throw new Error("Mobile photo GUID was not received");
  }
  return guid;
};

// room is optional, the phone page only uses it for its texts ("...a new picture of your kitchen")
export const buildMobilePhotoUrl = (guid: string, fabricatorId: string | number = document.kvConfig.userId): string => {
  // URLSearchParams always joins with a plain "&" (never "&amp;") and handles the encoding
  const url = new URL("/qr.html", window.location.origin);
  url.searchParams.set("fabId", String(fabricatorId));
  url.searchParams.set("guid", guid);
  url.searchParams.set("room", getCommonVisualizerType() || "");
  const urlBase = _getUrlBase();
  if (urlBase) { // for localhost
    url.searchParams.set("server", _getUrlBase());
  }
  return url.toString();
};

// polls every MOBILE_PHOTO_POLL_INTERVAL ms (setTimeout chaining, so slow responses never stack up),
// returns a stop function - call it on panel close / unmount
export const startMobilePhotoPolling = (guid: string, callbacks: MobilePhotoPollingCallbacks): (() => void) => {
  let stopped = false;
  let timer: number | undefined;
  let networkErrors = 0;
  const startedAt = Date.now();

  const stop = () => {
    stopped = true;
    window.clearTimeout(timer);
  };

  const tick = async () => {
    if (stopped) {
      return;
    }
    if (Date.now() - startedAt > MOBILE_PHOTO_POLL_TIMEOUT) {
      stop();
      callbacks.onExpired();
      return;
    }
    try {
      const response = await JsonApi.getMobilePhotoResult(guid);
      if (stopped) {
        return;
      }
      networkErrors = 0;
      const imageUrl = response?.data;
      if (imageUrl && typeof imageUrl === "string") {
        stop();
        callbacks.onImage(imageUrl);
        return;
      }
    } catch (error) {
      if (stopped) {
        return;
      }
      if (getHttpStatus(error) === 404) { // unknown GUID - no reason to keep polling
        stop();
        callbacks.onError("notFound");
        return;
      }
      networkErrors++;
      if (networkErrors >= MOBILE_PHOTO_MAX_NETWORK_ERRORS) {
        stop();
        callbacks.onError("network");
        return;
      }
    }
    timer = window.setTimeout(tick, MOBILE_PHOTO_POLL_INTERVAL);
  };

  timer = window.setTimeout(tick, MOBILE_PHOTO_POLL_INTERVAL);
  return stop;
};

// the AI flow needs a real File (createCommonPostData -> toBase64(userFile) + userFile.name),
// NOTE: the image host has to send CORS headers for the visualizer origins
export const imageUrlToFile = async (imageUrl: string): Promise<File> => {
  const response = await fetch(imageUrl, {mode: "cors"});
  if (!response.ok) {
    throw new Error(`Mobile photo download failed: ${response.status}`);
  }
  const blob = await response.blob();
  const type = blob.type && blob.type.startsWith("image/") ? blob.type : "image/jpeg";
  const extension = type.split("/")[1].replace("jpeg", "jpg").split("+")[0];
  return new File([blob], `mobile-photo-${Date.now()}.${extension}`, {type});
};
